import React, { Suspense } from "react";
import {
  Box,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  Image,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { useClaimDrawerStore } from "@/features/my-order/features/order-history/stores/useClaimDrawerStore";
import useGetClaimDetail from "@/features/my-order/features/order-history/features/claim/hooks/useGetClaimDetail";
import { useRecoilValue } from "recoil";
import claimDetailIdStore from "@/features/my-order/features/order-history/stores/useClaimDetailIdStore";
import CenteredSpinner from "@/shared/components/centered-spinner";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import CustomIcon from "../../../../../../../../public/icons-v1_1";
import { formatDateToYMDEGeneral } from "@/shared/utils";
import OrderHistoryButton from "@/features/my-order/features/order-history/components/OrderHistoryButton";
import OrderHistoryClaimDetailEditModal from "@/features/my-order/features/order-history/features/claim/components/OrderHistoryClaimDetailEditModal";
import OrderHistoryClaimDetailWithdrawModal from "@/features/my-order/features/order-history/features/claim/components/OrderHistoryClaimDetailWithdrawModal";
import ClaimStatusBadge from "@/features/my-order/components/ClaimStatusBadge";
import { ClaimStatus } from "@/features/my-order/features/order-history/features/claim/types";

function OrderHistoryClaimDetailDrawerWrapper() {
  const { isOpen, onClose } = useClaimDrawerStore();
  const detailId = useRecoilValue(claimDetailIdStore);

  return (
    <>
      {isOpen && detailId && (
        <ClaimDetailDrawer isOpen={isOpen} onClose={onClose} />
      )}
    </>
  );
}

export default OrderHistoryClaimDetailDrawerWrapper;

interface Props {
  children?: React.ReactNode;
  isOpen: boolean;
  onClose: () => void;
}

const ClaimDetailDrawer = ({ isOpen, onClose }: Props) => {
  return (
    <Drawer isOpen={isOpen} placement="right" onClose={onClose}>
      <DrawerOverlay />
      <DrawerContent minWidth={"480px"}>
        <Suspense fallback={<CenteredSpinner />}>
          <Content onClose={onClose} />
        </Suspense>
      </DrawerContent>
    </Drawer>
  );
};

const Content = ({ onClose }: Partial<Props>) => {
  const { tTitle } = useI18nHelper();
  const detailId = useRecoilValue(claimDetailIdStore);
  const { data } = useGetClaimDetail(detailId);

  const {
    isOpen: isEditModalOpen,
    onOpen: onEditModalOpen,
    onClose: onEditModalClose,
  } = useDisclosure();
  const {
    isOpen: isWithdrawModalOpen,
    onOpen: onWithdrawModalOpen,
    onClose: onWithdrawModalClose,
  } = useDisclosure();

  return (
    <>
      <DrawerHeader boxShadow={"0 0.5px 0 0 #D4D4D1"} padding={"12px 16px"}>
        <Flex justifyContent={"space-between"} alignItems={"center"}>
          <Text textStyle={"body-md-bold"} color={"#444444"}>
            {tTitle("order_history:claim.claim_info")}
          </Text>
          <Box
            color={"#8F8F8C"}
            _hover={{
              color: "#1272EF",
            }}
            _active={{
              color: "#00417D",
            }}
          >
            <CustomIcon src={"x"} width={24} height={24} onClick={onClose} />
          </Box>
        </Flex>
      </DrawerHeader>
      <DrawerBody padding={"16px"}>
        <ClaimDetailDrawerBody />
      </DrawerBody>
      {data.claimRequest.status === "PENDING" && (
        <DrawerFooter padding={0} boxShadow={"0 -0.5px 0 0 #D4D4D1"}>
          <Flex
            padding={"16px"}
            paddingTop={"12px"}
            alignItems={"center"}
            gap={"12px"}
            flex={1}
          >
            <OrderHistoryButton
              leadingIconSrc={"edit"}
              width={"100%"}
              onClick={() => onEditModalOpen()}
            >
              {tTitle("edit")}
            </OrderHistoryButton>
            <OrderHistoryButton
              width={"100%"}
              onClick={() => onWithdrawModalOpen()}
            >
              {tTitle("withdraw")}
            </OrderHistoryButton>
          </Flex>
        </DrawerFooter>
      )}
      <OrderHistoryClaimDetailEditModal
        isOpen={isEditModalOpen}
        onClose={onEditModalClose}
      />
      <OrderHistoryClaimDetailWithdrawModal
        isOpen={isWithdrawModalOpen}
        onClose={onWithdrawModalClose}
      />
    </>
  );
};

const ClaimDetailDrawerBody = () => {
  const { tTitle, isKor } = useI18nHelper();
  const detailId = useRecoilValue(claimDetailIdStore);
  const { data } = useGetClaimDetail(detailId);
  const { imageUrl, brandName, sku, size, category, colorName, madeIn } =
    data?.productInformation;
  const { type, content, images, status } = data?.claimRequest;
  const { responseAt, content: responseContent } = data?.claimResponse;

  const productInfoList = [
    { title: "category", value: category },
    { title: "color", value: colorName },
    { title: "order_history:confirm.origin", value: madeIn },
  ];

  const isUpdated = data?.updatedAt !== data?.createdAt;

  const statusResultMap: Partial<
    Record<
      ClaimStatus,
      { date?: string; backgroundColor: string; text: string }
    >
  > = {
    COMPLETED: {
      date: responseAt,
      backgroundColor: "#FFF7CC",
      text: tTitle("completed"),
    },
    CANCELED: {
      date: data?.canceledAt,
      backgroundColor: "#FEDCDC",
      text: tTitle("order_history:claim.status_canceled"),
    },
    REJECTED: {
      date: responseAt,
      backgroundColor: "#FEDCDC",
      text: tTitle("order_history:claim.status_rejected"),
    },
  };

  return (
    <Flex flexDirection={"column"} gap={"24px"}>
      <Flex flexDirection={"column"} gap={"8px"}>
        <Text textStyle={"body-lg-semi-bold"} color={"#444444"}>
          {tTitle("order_history:claim.product_info")}
        </Text>
        <Flex gap={"12px"} alignItems={"center"}>
          <Box
            width={"68px"}
            height={"68px"}
            minWidth={"68px"}
            borderRadius={"4px"}
            overflow={"hidden"}
            boxShadow={"0 0 0 0.5px #D4D4D1"}
          >
            <Image
              src={imageUrl}
              alt={"product"}
              fallbackSrc={"/images/placeholder-product.png"}
              objectFit={"cover"}
              width={"100%"}
              height={"100%"}
            />
          </Box>
          <Flex flexDirection={"column"}>
            <Text textStyle={"body-sm-medium"} color={"#444444"}>
              {brandName}
            </Text>
            <Text textStyle={"body-md-bold"} color={"#444444"}>
              {sku}
            </Text>
            <Text textStyle={"body-sm-medium"} color={"#6F6F6C"}>
              {size}
            </Text>
          </Flex>
        </Flex>
        <Flex flexDirection={"column"} paddingX={"4px"}>
          {productInfoList.map((info, index) => (
            <Flex gap={"6px"} alignItems={"center"} key={index}>
              <Text
                textStyle={"body-sm-medium"}
                color={"#6F6F6C"}
                width={"68px"}
              >
                {tTitle(info.title)}
              </Text>
              <Text textStyle={"body-md-medium"} color={"#363636"}>
                {info.value ?? "-"}
              </Text>
            </Flex>
          ))}
        </Flex>
      </Flex>
      <Flex flexDirection={"column"} gap={"8px"}>
        <Flex justifyContent={"space-between"} alignItems={"center"}>
          <Text textStyle={"body-lg-semi-bold"} color={"#444444"}>
            {tTitle("order_history:claim.claim_info")}
          </Text>
          <ClaimStatusBadge status={status} />
        </Flex>
        {statusResultMap[status] && (
          <Flex
            padding={"12px"}
            borderRadius={"4px"}
            background={statusResultMap[status]?.backgroundColor}
            flexDirection={"column"}
            gap={"8px"}
          >
            <Flex justifyContent={"space-between"} alignItems={"center"}>
              <Flex gap={"4px"} alignItems={"center"}>
                <CustomIcon
                  src={"head-set"}
                  width={20}
                  height={20}
                  color={"#8F8F8C"}
                />
                <Text textStyle={"body-sm-bold"} color={"#444444"}>
                  {tTitle("order_history:claim.process_content")}
                </Text>
              </Flex>

              <Text textStyle={"body-sm-regular"} color={"#6F6F6C"}>
                {formatDateToYMDEGeneral(
                  statusResultMap[status]?.date,
                  isKor,
                  "-"
                )}{" "}
                {statusResultMap[status]?.text}
              </Text>
            </Flex>
            {responseContent && (
              <>
                {/*<Text textStyle={'body-md-medium'}>{tTitle("order_history:claim.result")} : {}</Text>*/}
                <Text textStyle={"body-md-medium"} color={"#363636"}>
                  {responseContent}
                </Text>
              </>
            )}
          </Flex>
        )}
        <Flex
          padding={"12px"}
          borderRadius={"4px"}
          background={"#F7F9FA"}
          flexDirection={"column"}
          gap={"8px"}
          overflow={"auto"}
        >
          <Flex justifyContent={"space-between"} alignItems={"center"} flex={1}>
            <Flex gap={"4px"} alignItems={"center"}>
              <CustomIcon
                src={"frame"}
                width={20}
                height={20}
                color={"#8F8F8C"}
              />
              <Text textStyle={"body-sm-bold"} color={"#444444"}>
                {tTitle("order_history:claim.claim_reason")}
              </Text>
            </Flex>
            <Text textStyle={"body-sm-regular"} color={"#6F6F6C"}>
              {formatDateToYMDEGeneral(
                isUpdated ? data?.updatedAt : data?.createdAt,
                isKor,
                "-"
              )}{" "}
              {isUpdated ? tTitle("modified") : tTitle("requested")}
            </Text>
          </Flex>
          <Text textStyle={"body-md-medium"} color={"#6F6F6C"}>
            {tTitle("order_history:claim.type")} :{" "}
            {tTitle(`order_history:claim.type_${type.toLowerCase()}_title`)}
          </Text>
          <Flex flexDirection={"column"} gap={"12px"}>
            <Flex gap={"8px"} flexWrap={"wrap"}>
              {images?.map((image, index) => (
                <Box
                  key={index}
                  width={"78px"}
                  height={"78px"}
                  minWidth={"78px"}
                  borderRadius={"4px"}
                  overflow={"hidden"}
                  boxShadow={"0 0 0 0.5px #D4D4D1"}
                >
                  <Image
                    src={image.url}
                    alt={`claim-image-${index}`}
                    fallbackSrc={"/images/placeholder-product.png"}
                    objectFit={"cover"}
                    width={"100%"}
                    height={"100%"}
                  />
                </Box>
              ))}
            </Flex>
            {content && (
              <Text textStyle={"body-md-medium"} color={"#363636"}>
                {content}
              </Text>
            )}
          </Flex>
        </Flex>
      </Flex>
    </Flex>
  );
};
